import { get } from "svelte/store"
import {
  ControlClientError,
  ControlWebClient,
  type ControlClientErrorOptions,
  type MountedControlWebClient,
  type RequestOptions,
  type ControlWebClientOptions,
} from "@kontextwerk/control-web-client"
import { apiBaseOverride } from "../../../_shared/_logic/store"
import { baseAPIURL } from "../../_shared/_logic/config"

const DEFAULT_PREFIX = "/api/v1"

type BaseConfig = {
  baseUrl: string
  apiPrefix: string
}

const splitBaseUrl = (raw: string): BaseConfig => {
  try {
    const parsed = new URL(raw)
    const apiPrefix = parsed.pathname.replace(/\/+$/, "") || DEFAULT_PREFIX

    return { baseUrl: parsed.origin, apiPrefix }
  } catch {
    const normalized = raw.replace(/\/+$/, "")
    const match = normalized.match(/^(https?:\/\/[^/]+)(\/.*)$/)

    if (match) {
      const baseUrl = match[1]
      const apiPrefix = match[2] || DEFAULT_PREFIX

      return { baseUrl, apiPrefix: apiPrefix === "/" ? DEFAULT_PREFIX : apiPrefix }
    }

    return { baseUrl: normalized || "http://localhost:8000", apiPrefix: DEFAULT_PREFIX }
  }
}

const normalizeAdminPath = (path: string): string => {
  if (path.startsWith("/admin")) return path

  if (!path.startsWith("/")) return `/admin/${path}`

  return `/admin${path}`
}

type KehrblattRawApi = {
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>
  requestWithMeta<T>(
    method: string,
    path: string,
    options?: RequestOptions,
  ): Promise<{ data: T; headers: Headers; status: number }>
}

type KehrblattAdminApi = {
  raw: KehrblattRawApi
}

type KehrblattMount = {
  kehrblatt: {
    admin: KehrblattAdminApi
  }
}

type KehrblattControlWebClient = MountedControlWebClient<KehrblattMount>

const createKehrblattRawApi = (client: ControlWebClient): KehrblattRawApi => ({
  request<T>(method: string, path: string, options: RequestOptions = {}) {
    return client.transport.request<T>(method, normalizeAdminPath(path), {
      ...options,
      withAuth: options.withAuth ?? true,
    })
  },
  requestWithMeta<T>(method: string, path: string, options: RequestOptions = {}) {
    return client.transport.requestWithMeta<T>(method, normalizeAdminPath(path), {
      ...options,
      withAuth: options.withAuth ?? true,
    })
  },
})

let cachedClient: KehrblattControlWebClient | null = null
let cachedBase = ""
let cachedPrefix = ""

const resolveBase = (): BaseConfig => {
  const override = get(apiBaseOverride)
  const rawBase = (override && override.trim()) || baseAPIURL

  return splitBaseUrl(rawBase)
}

export const getControlWebClient = (
  options?: Partial<ControlWebClientOptions>,
): KehrblattControlWebClient => {
  const { baseUrl, apiPrefix } = resolveBase()

  if (!cachedClient || cachedBase !== baseUrl || cachedPrefix !== apiPrefix) {
    cachedClient = new ControlWebClient({
      ...options,
      baseUrl,
      apiPrefix,
      timeoutMs: 120_000,
      retry: {
        maxAttempts: 1,
        backoffMs: 0,
        retryStatusCodes: [],
      },
    }).mount("kehrblatt", ({ client }) => ({
      admin: {
        raw: createKehrblattRawApi(client),
      },
    }))
    cachedBase = baseUrl
    cachedPrefix = apiPrefix
  }

  return cachedClient
}

export const getAdminApi = () => getControlWebClient().admin
export const getPublicApi = () => getControlWebClient().public
export const getKehrblattAdminApi = () => getControlWebClient().kehrblatt.admin

export const buildQueryString = (
  params: Record<
    string,
    string | number | boolean | Array<string | number | boolean> | undefined | null
  >,
) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== "") {
          search.append(key, String(entry))
        }
      })
      continue
    }

    search.set(key, String(value))
  }
  const qs = search.toString()

  return qs ? `?${qs}` : ""
}

export const serializeCsvQuery = (
  values?: Array<string | number | boolean | null | undefined> | null,
): string | undefined => {
  if (!values?.length) return undefined

  const filtered = values
    .map((value) => (value === undefined || value === null ? "" : String(value).trim()))
    .filter(Boolean)

  return filtered.length ? filtered.join(",") : undefined
}

export class ApiError extends ControlClientError {
  constructor(
    status: number,
    message: string,
    payload?: unknown,
    options?: ControlClientErrorOptions,
  ) {
    super(message, {
      statusCode: status,
      detail: payload ?? options?.detail,
      responseText: options?.responseText,
      reasonCode: options?.reasonCode,
      requestId: options?.requestId,
      fieldErrors: options?.fieldErrors,
    })
    this.status = status
    this.payload = payload ?? options?.detail
  }
}

const inflightReads = new Map<string, Promise<unknown>>()

const readKey = (method: string, path: string, init: RequestInit) => {
  // A caller-owned signal also owns that request's lifetime. Sharing the
  // promise would let one unmount abort an otherwise independent consumer.
  if (method !== "GET" || init.body || init.signal) return null

  const headers = new Headers(init.headers ?? undefined)

  return JSON.stringify({
    base: resolveBase(),
    method,
    path,
    headers: Array.from(headers.entries()).sort(([left], [right]) => left.localeCompare(right)),
  })
}

const dedupeRead = async <T>(key: string | null, loader: () => Promise<T>): Promise<T> => {
  if (!key) return loader()

  const existing = inflightReads.get(key)

  if (existing) return existing as Promise<T>

  const next = loader().finally(() => {
    inflightReads.delete(key)
  })
  inflightReads.set(key, next)

  return next
}

const requestHeaders = (init: RequestInit): Headers => {
  const headers = new Headers(init.headers)

  if (typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }

  return headers
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase()
  const headers = requestHeaders(init)
  const body = init.body ?? undefined

  try {
    return await dedupeRead(readKey(method, path, init), () =>
      getKehrblattAdminApi().raw.request<T>(method, path, {
        body,
        headers: Object.fromEntries(headers.entries()),
        signal: init.signal,
      }),
    )
  } catch (err) {
    if (err instanceof ControlClientError) {
      throw new ApiError(err.statusCode ?? err.status ?? 0, err.message, err.detail, {
        statusCode: err.statusCode ?? err.status,
        detail: err.detail,
        responseText: err.responseText,
        reasonCode: err.reasonCode,
        requestId: err.requestId,
        fieldErrors: err.fieldErrors,
      })
    }

    throw err
  }
}

export async function requestWithHeaders<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ data: T; headers: Headers; status: number }> {
  const method = (init.method ?? "GET").toUpperCase()
  const headers = requestHeaders(init)

  return dedupeRead(readKey(method, path, init), () =>
    getKehrblattAdminApi().raw.requestWithMeta<T>(method, path, {
      body: init.body ?? undefined,
      headers: Object.fromEntries(headers.entries()),
      signal: init.signal,
    }),
  )
}

export async function requestBlobWithHeaders(
  path: string,
  init: RequestInit = {},
): Promise<{ data: Blob; headers: Headers; status: number }> {
  const method = (init.method ?? "GET").toUpperCase()
  const headers = requestHeaders(init)
  try {
    return await getKehrblattAdminApi().raw.requestWithMeta<Blob>(method, path, {
      body: init.body ?? undefined,
      headers: Object.fromEntries(headers.entries()),
      signal: init.signal,
      responseType: "blob",
    })
  } catch (err) {
    if (err instanceof ControlClientError) {
      throw new ApiError(err.statusCode ?? err.status ?? 0, err.message, err.detail, {
        statusCode: err.statusCode ?? err.status,
        detail: err.detail,
        responseText: err.responseText,
        reasonCode: err.reasonCode,
        requestId: err.requestId,
        fieldErrors: err.fieldErrors,
      })
    }

    throw err
  }
}
